import React, { useRef } from 'react'
import {
  UseFormRegister,
  UseFormHandleSubmit,
  UseFormSetValue,
  useWatch,
  Control,
} from 'react-hook-form'
import { TextInput } from '../../../components/form/inputs/TextInput'
import {
  SlideBusinessAbstract,
  BusinessAbstractFormData,
} from '../hooks/business_abstract/types'
import { SlideContainer } from '../components/slide_container'
import { SlideHeader } from '../components/slide_header'
import {
  AiRegenerateSection,
  AiInstructionFormData,
} from '../components/ai_regenerate_section'
import { ListGroup } from '../components/list/list_group'
import { useImageUpload } from '../../../hooks/use_image_upload'
import logoGray from '../../../images/logo_gray.png'
import NoImage from '../../../images/noimage.png'

interface BusinessAbstractSlideProps {
  businessAbstract: SlideBusinessAbstract
  isEditing: boolean
  isUpdating: boolean
  isDirty: boolean
  register: UseFormRegister<BusinessAbstractFormData>
  handleSubmit: UseFormHandleSubmit<BusinessAbstractFormData>
  setValue: UseFormSetValue<BusinessAbstractFormData>
  control: Control<BusinessAbstractFormData>
  registerAi: UseFormRegister<AiInstructionFormData>
  handleSubmitAi: UseFormHandleSubmit<AiInstructionFormData>
  aiInstruction: string
  onFormSubmit: (data: BusinessAbstractFormData) => Promise<void>
  onAiRegenerate: (data: AiInstructionFormData) => Promise<void>
}

export const BusinessAbstractSlide = React.forwardRef<
  HTMLDivElement,
  BusinessAbstractSlideProps
>(
  (
    {
      businessAbstract,
      isEditing,
      isUpdating,
      isDirty,
      register,
      handleSubmit,
      setValue,
      control,
      registerAi,
      handleSubmitAi,
      aiInstruction,
      onFormSubmit,
      onAiRegenerate,
    },
    ref
  ) => {
    const thumbnailFileInputRef = useRef<HTMLInputElement | null>(null)
    const { upload } = useImageUpload()

    const currentThumbnailUrl = useWatch({
      control,
      name: 'thumbnailUrl',
    })

    const handleThumbnailImageUpload = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const file = event.target.files?.[0]
      if (!file) return

      const response = await upload(file)

      if (response !== null) {
        setValue('thumbnailUrl', response.url, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
    }

    return (
      <div ref={ref}>
        <SlideContainer>
          <SlideHeader
            title="1. 事業概要"
            isEditing={isEditing}
            isUpdating={isUpdating}
            isDirty={isDirty}
            onUpdate={handleSubmit(onFormSubmit)}
          />
          <div className="slide-content px-8 py-6">
            <div className="mb-6">
              {isEditing ? (
                <div className="flex items-center">
                  <span className="text-xl font-bold mr-2">
                    プロジェクト名：
                  </span>
                  <div className="flex-1">
                    <TextInput register={register('projectName')} />
                  </div>
                </div>
              ) : (
                <p className="text-xl font-bold">
                  プロジェクト名：{businessAbstract.projectName}
                </p>
              )}
            </div>
            <div className="flex mb-6">
              <div className="flex-shrink-0 mr-8">
                <img
                  src={
                    currentThumbnailUrl ||
                    businessAbstract.thumbnailUrl ||
                    NoImage
                  }
                  alt={businessAbstract.projectName}
                  className="w-64 h-64"
                />
                {isEditing && (
                  <>
                    <label
                      htmlFor="thumbnail-image-upload"
                      className="text-main-green p-2 border border-main-green cursor-pointer inline-block text-sm hover:opacity-50 transition-opacity duration-300 rounded-md w-full mt-6 text-center"
                    >
                      画像を変更
                    </label>
                    <input
                      id="thumbnail-image-upload"
                      ref={thumbnailFileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleThumbnailImageUpload}
                    />
                  </>
                )}
              </div>
              <div className="w-full">
                <ListGroup<BusinessAbstractFormData>
                  title="事業の目的と市場機会"
                  items={[
                    {
                      label: '市場背景',
                      value: businessAbstract.sections.marketBackground,
                      fieldName: 'marketBackground',
                    },
                    {
                      label: '競合分析',
                      value: businessAbstract.sections.competitorAnalysis,
                      fieldName: 'competitorAnalysis',
                    },
                    {
                      label: 'ビジネスチャンス',
                      value: businessAbstract.sections.businessChance,
                      fieldName: 'businessChance',
                    },
                  ]}
                  isEditing={isEditing}
                  register={register}
                />
                <div className="mb-6" />
                <ListGroup<BusinessAbstractFormData>
                  title="事業内容"
                  items={[
                    {
                      label: '',
                      value: businessAbstract.sections.businessContentFirst,
                      fieldName: 'businessContentFirst',
                    },
                    {
                      label: '',
                      value: businessAbstract.sections.businessContentSecond,
                      fieldName: 'businessContentSecond',
                    },
                  ]}
                  isEditing={isEditing}
                  register={register}
                />
              </div>
            </div>
            {isEditing && (
              <AiRegenerateSection
                register={registerAi}
                handleSubmit={handleSubmitAi}
                aiInstruction={aiInstruction}
                isUpdating={isUpdating}
                onSubmit={onAiRegenerate}
              />
            )}
            <div className="slide-footer mt-20">
              <p className="text-xs text-main-darkgray text-center mb-2">
                この企画書は「SDGs 共創
                Lodge」の共創アイディア生成AIで生成しました。
              </p>
              <img
                src={logoGray}
                alt="SDGs共創Lodge Logo"
                className="absolute bottom-2 right-4 w-20 h-auto"
              />
            </div>
          </div>
        </SlideContainer>
      </div>
    )
  }
)

BusinessAbstractSlide.displayName = 'BusinessAbstractSlide'
